import { css } from '@emotion/react'
import { Flow, Heading, Theme, useTheme } from 'bold-ui'
import { HideOnScreenSize } from 'components/responsive'
import { StreamPlayer } from 'components/stream-player'
import { useCallback, useEffect, useState } from 'react'

import { AcaoVideochamadaButton } from '../componentes/AcaoVideochamadaButton'
import { useMediaDevicesConfiguration } from '../hooks/useMediaDevicesConfiguration'
import { useUserMedia } from '../hooks/useUserMedia'
import { useConfiguracoesVideochamada } from '../hooks/useVideochamadaStorage'
import { ConfiguracoesVideochamadaModel, MediaStreamTrackKind } from '../model-videochamada'
import { ConfiguracoesVideochamadaFormModal } from './ConfiguracoesVideochamadaFormModal'
import { MicrophoneFeedback } from './MicrophoneFeedback'

interface CameraPreviewProps {
  audioEnabled: boolean
  videoEnabled: boolean
  setAudioEnabled(value: boolean): void
  setVideoEnabled(value: boolean): void
  isMobile?: boolean
}

export function CameraPreview(props: CameraPreviewProps) {
  const theme = useTheme()

  const { audioEnabled, videoEnabled, setAudioEnabled, setVideoEnabled, isMobile } = props

  const [isConfiguracoesModalOpen, setConfiguracoesModalOpen] = useState(false)
  const [configuracoesVideochamada, setConfiguracoesVideochamada] = useConfiguracoesVideochamada()

  const handleVideoRevoked = useCallback(() => setVideoEnabled(false), [setVideoEnabled])
  const handleAudioRevoked = useCallback(() => setAudioEnabled(false), [setAudioEnabled])

  const { stream: localStream, audioDeviceAvailable, videoDeviceAvailable, mediaDevices } = useUserMedia({
    video: videoEnabled,
    audio: audioEnabled,
    videoDeviceId: configuracoesVideochamada?.videoInput?.id,
    audioDeviceId: configuracoesVideochamada?.audioInput?.id,
    onVideoRevoked: handleVideoRevoked,
    onAudioRevoked: handleAudioRevoked,
  })

  const styles = createStyles(theme)

  const [videoTrackEnabled, setVideoTrackEnabled] = useState(false)
  const [audioTrackEnabled, setAudioTrackEnabled] = useState(false)

  useEffect(() => {
    const handleChangeTracks = (e: CustomEvent | MediaStreamTrack) => {
      const track = e instanceof CustomEvent ? e.detail?.track : (e as MediaStreamTrack)
      if (!track) return

      if (track.kind === MediaStreamTrackKind.AUDIO) setAudioTrackEnabled(track.enabled)
      else setVideoTrackEnabled(track.enabled)
    }

    const listener = (e: Event) => handleChangeTracks(e as CustomEvent)

    if (localStream) {
      localStream.addEventListener('enable', listener)
      localStream.getTracks().forEach((track) => handleChangeTracks(track))
    } else {
      setVideoTrackEnabled(false)
      setAudioTrackEnabled(false)
    }

    return () => {
      if (localStream) {
        localStream.removeEventListener('enable', listener)
      }
    }
  }, [localStream])

  const { mediaDevicesConfiguration, setStoredAudioDevice, setStoredVideoDevice } = useMediaDevicesConfiguration({
    mediaDevices,
    audioEnabled,
    videoEnabled,
  })

  const handleSetConfiguracoesVideochamada = (config: ConfiguracoesVideochamadaModel) => {
    setConfiguracoesVideochamada(config)
    setStoredAudioDevice(config.audioInput)
    setStoredVideoDevice(config.videoInput)
  }

  return (
    <div css={[styles.container, isMobile && styles.containerMobile]}>
      <ConfiguracoesVideochamadaFormModal
        open={isConfiguracoesModalOpen}
        onClose={() => setConfiguracoesModalOpen(false)}
        initialConfigurations={mediaDevicesConfiguration}
        onSubmit={handleSetConfiguracoesVideochamada}
        audioEnabled={audioEnabled}
        videoEnabled={videoEnabled}
      />
      <div css={styles.cameraAndButtons}>
        <div css={styles.camera}>
          <StreamPlayer style={styles.video} stream={localStream} playsInline muted autoPlay />
          {!videoTrackEnabled && (
            <div css={styles.noCameraOverlay}>
              <Heading
                level={1}
                style={css`
                  color: white;
                `}
              >
                Sem câmera
              </Heading>
            </div>
          )}
        </div>

        <MicrophoneFeedback
          audioTrack={localStream?.getAudioTracks()[0]}
          audioEnabled={audioTrackEnabled}
          styles={styles.micFeedback}
        />
        <Flow direction='horizontal' style={styles.buttons}>
          <AcaoVideochamadaButton
            unavailable={!audioDeviceAvailable}
            state={audioDeviceAvailable && audioEnabled ? 'on' : 'off'}
            onClick={() => setAudioEnabled(!audioEnabled)}
            icon='mic'
          />
          <AcaoVideochamadaButton
            unavailable={!videoDeviceAvailable}
            state={videoDeviceAvailable && videoEnabled ? 'on' : 'off'}
            onClick={() => setVideoEnabled(!videoEnabled)}
            icon='video'
          />
          <HideOnScreenSize down='sm'>
            <AcaoVideochamadaButton state='on' onClick={() => setConfiguracoesModalOpen(true)} icon='config' />
          </HideOnScreenSize>
        </Flow>
      </div>
    </div>
  )
}

const createStyles = (theme: Theme) => ({
  container: css`
    padding-top: 56.25%;
    position: relative;
    height: 100%;
    max-height: 19.4rem;
    @media (max-width: ${theme.breakpoints.size['sm']}px) {
      padding-top: 0;
      height: auto;
      max-height: none;
    }
  `,
  cameraAndButtons: css`
    position: absolute;
    width: 100%;
    height: 100%;
    bottom: 0;
    @media (max-width: ${theme.breakpoints.size['sm']}px) {
      position: relative;
      height: auto;
      bottom: auto;
    }
  `,
  video: css`
    display: block;
    height: 100%;
    width: 100%;
    object-fit: contain;
    @media (max-width: ${theme.breakpoints.size['sm']}px) {
      height: auto;
      aspect-ratio: 9 / 16;
    }
  `,
  camera: css`
    height: 100%;
    position: relative;
    isolation: isolate;
    overflow: hidden;
    background-color: ${theme.pallete.gray.c20};
    @media (max-width: ${theme.breakpoints.size['sm']}px) {
      height: auto;
    }
  `,
  noCameraOverlay: css`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${theme.pallete.gray.c20};
  `,
  buttons: css`
    width: 100%;
    height: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    bottom: 0;
    padding: 1rem;
  `,
  micFeedback: css`
    position: absolute;
    top: 1rem;
    left: 1rem;
  `,
  containerMobile: css`
    padding-top: 0;
    max-height: none;
    min-height: calc(8.375rem - (100vh - ${window.innerHeight}px));
  `,
})
